import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AccountTreePicker } from '../../components/ui/AccountTreePicker';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { StickyNav } from '../../components/ui/StickyNav';
import { DriveAssetsBrowser } from '../../components/drive/DriveAssetsBrowser';
import type {
  AssetLabel,
  Campaign,
  CampaignLeadForm,
  CampaignStatus,
  CtaOption,
  GoogleAssets,
  LandingPageOverride,
  LandingPageType,
  MetaAdCopy,
  MetaAdType,
  UtmConfig,
} from '../../types/campaign';
import { ApiError, type MissingField } from '../../lib/api';
import type { Account } from '../../types/account';
import type { CampaignOfferFacts } from '../../types/campaign';
import { listAccounts } from '../accounts/api';
import { getFranchiseProfile } from '../franchise/api';
import {
  addCampaignAssetLabelStubs,
  changeCampaignStatus,
  createCampaign,
  getCampaignDefaults,
  updateCampaign,
  type CampaignCreatePayload,
  type CampaignUpdatePayload,
} from './api';
import { BodyCopyArrayEditor } from './editors/BodyCopyArrayEditor';
import { HeadlineArrayEditor } from './editors/HeadlineArrayEditor';
import { DescriptionArrayEditor } from './editors/DescriptionArrayEditor';
import { LeadFormTabStrip } from './editors/LeadFormTabStrip';
import { GoogleAssetsEditor } from './editors/GoogleAssetsEditor';
import { PlaceholderHint } from './editors/PlaceholderHint';
import { LandingPageOverrideEditor } from './editors/LandingPageOverrideEditor';
import { LandingPagePreview } from './editors/LandingPagePreview';
import { UtmConfigEditor } from './editors/UtmConfigEditor';
import { campaignIncludesChannel, type CampaignChannels } from '../../lib/platformEnabled';
import { CHANNEL_OPTIONS, applyChannelChange } from '../../lib/campaignChannels';
import { resolveMasterLanding } from './landingPage';
import {
  CampaignCopyTrigger,
  CampaignCopyWorkspace,
  type CampaignCopyValues,
} from './CampaignCopyWorkspace';
import type { CampaignCopyFieldId } from '../../lib/campaignCopy/fields';

const CTA_OPTIONS: CtaOption[] = [
  'Get In Touch',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Sign Up',
  'Book Now',
  'Subscribe',
  'Apply Now',
  'Download',
];

const LANDING_TYPES: LandingPageType[] = ['offer', 'sale', 'location', 'custom'];

const META_AD_TYPE_OPTIONS: Array<{ value: MetaAdType; label: string; blurb: string }> = [
  { value: 'lead_gen', label: 'Meta Lead Gen', blurb: 'In-feed lead form. Landing page override is hidden.' },
  { value: 'traffic', label: 'Website Lead', blurb: 'Drives to the landing page. Lead form fields are hidden.' },
  { value: 'both', label: 'Both', blurb: 'Lead form ads + traffic ads. Both sets of fields apply.' },
];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export type CampaignFormMode =
  // Pre-P2b-3 polish (Fix 6): create can be seeded from an existing
  // Campaign via the duplicate button on CampaignDetail. duplicateSource
  // pre-fills every authored content field + shareTargets +
  // createdInBrandAccountId; the AM must still pick a unique slug and
  // typically renames before saving. isAlternate / isOptOut are NEVER
  // copied — designations are system-managed and exclusive per brand.
  | { kind: 'create'; brandAccountId: string | null; duplicateSource?: Campaign }
  | { kind: 'edit'; campaign: Campaign };

type FormState = {
  // Pre-P2b-3 §5. brandAccountId in the form state is the
  // organisational owner (createdInBrandAccountId on the wire). Renamed
  // semantically while keeping the existing UI label and field id so
  // the existing brand-picker dropdown works unchanged.
  brandAccountId: string;
  // Multi-share visibility (Pre-P2b-3 §5). Tree picker drives this list;
  // server requires min length 1. Defaults to [brand] on create so the
  // legacy single-owner behaviour is preserved when the AM doesn't
  // explicitly narrow.
  shareTargets: Array<{ accountId: string }>;
  // Which ad channels the campaign runs on. Choosing one HIDES the other
  // channel's sections; content already entered there is kept in state and
  // saved, so switching back restores it.
  channels: CampaignChannels;
  name: string;
  slug: string;
  slugTouched: boolean;
  description: string;
  landingPageType: LandingPageType;
  customLandingPageUrl: string;
  metaAdCopy: MetaAdCopy;
  googleAssets: GoogleAssets;
  googleLandingPageOverride: LandingPageOverride;
  metaLandingPageOverride: LandingPageOverride;
  // Meta polish Item 3: free-text folder name for Meta ad naming.
  metaCreativeFolderName: string;
  metaAssetsFolderUrl: string;
  // P2c §4.1: per-Campaign Meta asset labels. Empty array on legacy
  // Campaigns (server now always writes [], so the only case where this
  // can be undefined is a wire payload from before the field landed —
  // tolerated via `c.metaAssetLabels ?? []` at hydration time).
  metaAssetLabels: AssetLabel[];
  // ⚠️ The distilled offer mechanics (2026-08-27). Held HERE rather than written
  // by the copy service, so it behaves exactly like inserted copy: it survives
  // closing the workspace, and the ordinary audited campaign save is what
  // persists it. See src/lib/campaignCopy/offerFacts.ts.
  offerFacts: CampaignOfferFacts | null;
  googleAssetsFolderUrl: string;
  otherAssetsFolderUrl: string;
  utmConfig: UtmConfig;
  // Meta campaign naming pair (Pre-P2b-3 §1). Stored as '' for null on the
  // wire; trimmed at submit. Descriptor flows into {campaignDescriptor};
  // template (when set) overrides the brand default at render time.
  // Pre-P2b-3 polish round 5 §2.4: descriptor moves to the Overview
  // section since it's no longer Meta-specific (round 5 §2.1).
  campaignDescriptor: string;
  metaCampaignNameTemplate: string;
  // Pre-P2b-3 polish round 5 §2.2: Google ad-label template, parallel
  // to metaCampaignNameTemplate. New "Google Naming" section in §2.4.
  googleAdLabelTemplate: string;
  // Multi Lead Form spec §1.1. The Campaign holds an array of named lead
  // forms; one is the default. Commit 1 keeps the single-form editor on
  // entries[0]; commit 2 wires the tab strip authoring with +/rename/
  // delete/set-as-default. Both fields are required at save time — the
  // skeleton/defaults seed a single "Default" entry so create flows
  // never hit a zero-length array.
  campaignLeadForms: CampaignLeadForm[];
  defaultLeadFormId: string | null;
};

const EMPTY_UTM_CONFIG: UtmConfig = {
  enabled: false,
  platforms: [],
  shared: { campaignId: null, campaignName: null },
  meta: null,
  google: null,
};

const EMPTY_META: MetaAdCopy = {
  metaAdType: 'lead_gen',
  bodyCopy: [],
  headlines: [],
  descriptions: [],
  ctaButton: 'Learn More',
};

// Multi Lead Form spec §1.1. Generate a fresh CampaignLeadForm with a
// stable id. Used by the create path's blank state, and (commit 2) the
// tab strip's "+ Add form" affordance. Same pattern as the rest of the
// codebase (ColumnPresetMenu): crypto.randomUUID() with a coarse fallback.
function generateLeadFormId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `lf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildDefaultLeadForm(): CampaignLeadForm {
  return {
    id: generateLeadFormId(),
    name: 'Default',
    formType: 'Generic',
    fields: {
      headline: { mode: 'Generic' },
      description: { mode: 'Generic' },
      thankYouHeadline: { mode: 'Generic' },
      thankYouDescription: { mode: 'Generic' },
    },
  };
}

const EMPTY_GOOGLE: GoogleAssets = {
  headlines: [],
  descriptions: [],
  path1: '',
  path2: null,
};

function initialStateFromCampaign(c: Campaign): FormState {
  // Multi Lead Form spec §1.1. Legacy / partially-migrated Campaigns may
  // surface with an empty (or undefined) campaignLeadForms array — seed a
  // single Default so the editor never hits an empty array. defaultLeadFormId
  // falls back to the first entry's id when missing.
  const seeded =
    c.campaignLeadForms && c.campaignLeadForms.length > 0
      ? c.campaignLeadForms
      : [buildDefaultLeadForm()];
  const defaultId = c.defaultLeadFormId ?? seeded[0]!.id;
  return {
    brandAccountId: c.createdInBrandAccountId,
    shareTargets: c.shareTargets.length > 0
      ? c.shareTargets.map((t) => ({ accountId: t.accountId }))
      : [{ accountId: c.createdInBrandAccountId }],
    channels: c.channels ?? 'both',
    name: c.name,
    slug: c.slug,
    slugTouched: true,
    description: c.description ?? '',
    landingPageType: c.landingPageType,
    customLandingPageUrl: c.customLandingPageUrl ?? '',
    metaAdCopy: c.metaAdCopy,
    googleAssets: c.googleAssets,
    googleLandingPageOverride: c.googleLandingPageOverride ?? { type: 'inherit' },
    metaLandingPageOverride: c.metaLandingPageOverride ?? { type: 'inherit' },
    metaCreativeFolderName: c.metaCreativeFolderName ?? '',
    metaAssetsFolderUrl: c.metaAssetsFolderUrl ?? '',
    metaAssetLabels: c.metaAssetLabels ?? [],
    // Legacy campaigns predate the field — undefined reads as null.
    offerFacts: c.offerFacts ?? null,
    googleAssetsFolderUrl: c.googleAssetsFolderUrl ?? '',
    otherAssetsFolderUrl: c.otherAssetsFolderUrl ?? '',
    utmConfig: c.utmConfig ?? EMPTY_UTM_CONFIG,
    campaignDescriptor: c.campaignDescriptor ?? '',
    metaCampaignNameTemplate: c.metaCampaignNameTemplate ?? '',
    googleAdLabelTemplate: c.googleAdLabelTemplate ?? '',
    campaignLeadForms: seeded,
    defaultLeadFormId: defaultId,
  };
}

// Pre-P2b-3 polish (Fix 6): seed a fresh create state from an existing
// Campaign. Copies every authored field + shareTargets verbatim (Q9
// "duplicate preserves source state"), pre-fills name with " (copy)",
// leaves slug empty so the AM picks a fresh one (server enforces
// uniqueness per createdInBrandAccountId). Designations are NEVER
// copied — duplicates are normal Campaigns.
function blankCreateState(
  brandAccountId: string | null,
  duplicateSource?: Campaign,
): FormState {
  if (duplicateSource) {
    const sourceBrand = duplicateSource.createdInBrandAccountId;
    // Multi Lead Form spec §1.1: duplicating carries the source's full
    // lead form library verbatim. Each entry keeps its id so the
    // duplicate-source's row snapshots (none yet — but for symmetry)
    // would still point at valid entries. defaultLeadFormId carries
    // over too.
    const dupForms =
      duplicateSource.campaignLeadForms && duplicateSource.campaignLeadForms.length > 0
        ? duplicateSource.campaignLeadForms
        : [buildDefaultLeadForm()];
    const dupDefault = duplicateSource.defaultLeadFormId ?? dupForms[0]!.id;
    return {
      brandAccountId: sourceBrand,
      shareTargets: duplicateSource.shareTargets.map((t) => ({ accountId: t.accountId })),
      channels: duplicateSource.channels ?? 'both',
      name: `${duplicateSource.name} (copy)`,
      slug: '',
      slugTouched: false,
      description: duplicateSource.description ?? '',
      landingPageType: duplicateSource.landingPageType,
      customLandingPageUrl: duplicateSource.customLandingPageUrl ?? '',
      metaAdCopy: duplicateSource.metaAdCopy,
      googleAssets: duplicateSource.googleAssets,
      googleLandingPageOverride:
        duplicateSource.googleLandingPageOverride ?? { type: 'inherit' },
      metaLandingPageOverride:
        duplicateSource.metaLandingPageOverride ?? { type: 'inherit' },
      metaCreativeFolderName: duplicateSource.metaCreativeFolderName ?? '',
      metaAssetsFolderUrl: duplicateSource.metaAssetsFolderUrl ?? '',
      // Duplicating a Campaign carries the source's labels as a starting
      // point — the AM can edit them in the Assets section and save.
      metaAssetLabels: duplicateSource.metaAssetLabels ?? [],
      // ⚠️ A duplicate carries the offer facts over: it is the same offer, and
      // re-reading a 30-page PDF to arrive at the same block would be waste.
      offerFacts: duplicateSource.offerFacts ?? null,
      googleAssetsFolderUrl: duplicateSource.googleAssetsFolderUrl ?? '',
      otherAssetsFolderUrl: duplicateSource.otherAssetsFolderUrl ?? '',
      utmConfig: duplicateSource.utmConfig ?? EMPTY_UTM_CONFIG,
      campaignDescriptor: duplicateSource.campaignDescriptor ?? '',
      metaCampaignNameTemplate: duplicateSource.metaCampaignNameTemplate ?? '',
      googleAdLabelTemplate: duplicateSource.googleAdLabelTemplate ?? '',
      campaignLeadForms: dupForms,
      defaultLeadFormId: dupDefault,
    };
  }
  // Fresh-create seeding: a single "Default" lead form so the editor
  // always has at least one tab to render (commit 2's tab strip will
  // reuse this seed). defaultLeadFormId points at that one entry.
  const seed = buildDefaultLeadForm();
  return {
    brandAccountId: brandAccountId ?? '',
    // Default visibility: shared with the brand. Tree picker can narrow.
    shareTargets: brandAccountId ? [{ accountId: brandAccountId }] : [],
    channels: 'both',
    name: '',
    slug: '',
    slugTouched: false,
    description: '',
    landingPageType: 'offer',
    customLandingPageUrl: '',
    metaAdCopy: EMPTY_META,
    googleAssets: EMPTY_GOOGLE,
    googleLandingPageOverride: { type: 'inherit' },
    metaLandingPageOverride: { type: 'inherit' },
    metaCreativeFolderName: '',
    metaAssetsFolderUrl: '',
    metaAssetLabels: [],
    offerFacts: null,
    googleAssetsFolderUrl: '',
    otherAssetsFolderUrl: '',
    utmConfig: EMPTY_UTM_CONFIG,
    campaignDescriptor: '',
    metaCampaignNameTemplate: '',
    googleAdLabelTemplate: '',
    campaignLeadForms: [seed],
    defaultLeadFormId: seed.id,
  };
}

type SectionProps = { id: string; title: string; children: React.ReactNode };
function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="bg-white rounded-md border border-slate-200 p-6 space-y-4 scroll-mt-4">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

// Section nav anchors. Prefix `cf-` (Campaign Form) keeps the ids
// distinct from BuildView's bv-* and RowEditor's re-*. Each id is
// the DOM id on a <Section> rendered below; the StickyNav scroll-
// spy and click handlers anchor here.
// Sections that belong to one channel and are hidden when the campaign excludes it.
const META_SECTION_IDS = new Set(['cf-meta-naming', 'cf-meta', 'cf-lead-forms']);
const GOOGLE_SECTION_IDS = new Set(['cf-google-naming', 'cf-google']);

const SECTION_NAV: Array<{ id: string; label: string }> = [
  { id: 'cf-overview', label: 'Overview' },
  { id: 'cf-visibility', label: 'Visibility' },
  { id: 'cf-meta-naming', label: 'Meta naming' },
  { id: 'cf-google-naming', label: 'Google naming' },
  // AI copy generation (2026-08-26). Sits with the copy sections it writes
  // into, immediately above the first of them.
  { id: 'cf-ai-copy', label: 'AI copy' },
  { id: 'cf-meta', label: 'Meta ad copy' },
  // Multi Lead Form spec §1.1 + §2. Lead forms get their own section
  // (separate from Meta ad copy) — formType + leadForm no longer live
  // inside metaAdCopy.
  { id: 'cf-lead-forms', label: 'Lead forms' },
  { id: 'cf-google', label: 'Google assets' },
  { id: 'cf-assets', label: 'Asset folders' },
  { id: 'cf-utm', label: 'UTM tags' },
];

function Tooltip({ text }: { text: string }) {
  return (
    <span
      className="ml-1 text-xs text-slate-400 cursor-help"
      title={text}
      aria-label={text}
    >
      ⓘ
    </span>
  );
}

export function CampaignForm({ mode }: { mode: CampaignFormMode }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [brandIdForProfile, setBrandIdForProfile] = useState<string | null>(
    mode.kind === 'edit'
      ? mode.campaign.createdInBrandAccountId
      : (mode.duplicateSource?.createdInBrandAccountId ?? mode.brandAccountId),
  );
  // Fetch the selected Brand's FranchiseProfile so Overview + overrides can
  // preview the resolved landing-page template. Refetches when the brand
  // selection changes on create.
  const brandProfileQuery = useQuery({
    queryKey: ['franchiseProfile', brandIdForProfile],
    queryFn: () => getFranchiseProfile(brandIdForProfile!),
    enabled: !!brandIdForProfile,
  });
  const templates = brandProfileQuery.data?.landingPageTemplate ?? null;

  const [state, setState] = useState<FormState>(() =>
    mode.kind === 'edit'
      ? initialStateFromCampaign(mode.campaign)
      : blankCreateState(mode.brandAccountId, mode.duplicateSource),
  );
  const showMeta = campaignIncludesChannel(state.channels, 'meta');
  const showGoogle = campaignIncludesChannel(state.channels, 'google');

  // Lifecycle selector. Default draft on create; current status on edit.
  // Drives the Save button label and what the server should do after the
  // content save lands. 'active' clicks run the save + a status transition;
  // on activation failure the content IS saved (as whatever the current
  // status was) and the missing-fields list is surfaced to the user.
  const [targetStatus, setTargetStatus] = useState<CampaignStatus>(
    mode.kind === 'edit' ? mode.campaign.status : 'draft',
  );
  const [missing, setMissing] = useState<MissingField[]>([]);

  // ⚠️ AI COPY GENERATION (2026-08-26). ON-DEMAND ONLY: opening the workspace is
  // a click, generating inside it is another click, and nothing here is wired to
  // an effect or a query — this form re-renders on every keystroke of a long
  // authoring session. Pinned by campaignCopyOnDemandOnly.test.ts.
  const [copyOpen, setCopyOpen] = useState(false);

  // ⚠️ THE LIVE FORM VALUES, so the workspace's capacity and no-duplicate list
  // reflect what is on screen rather than what was last saved. An AM generates
  // mid-edit; anything else would offer them a headline they had just typed.
  const copyValues: CampaignCopyValues = {
    metaBodyCopy: state.metaAdCopy.bodyCopy,
    metaHeadlines: state.metaAdCopy.headlines,
    metaDescriptions: state.metaAdCopy.descriptions,
    googleHeadlines: state.googleAssets.headlines.map((h) => h.text),
    googleDescriptions: state.googleAssets.descriptions,
  };

  /**
   * Write one chosen option back into the form.
   *
   * ⚠️ It goes into the FORM, never to the server — the AM still reviews and
   * saves. And a Google headline is added as an UNPINNED entry: a pin position
   * is a deliberate RSA decision about where a headline must appear, and
   * inheriting one from whatever slot happened to be reused would be a targeting
   * change nobody asked for.
   */
  function applyCopy(field: CampaignCopyFieldId, next: string[]): void {
    setState((prev) => {
      switch (field) {
        case 'meta_body':
          return { ...prev, metaAdCopy: { ...prev.metaAdCopy, bodyCopy: next } };
        case 'meta_headline':
          return { ...prev, metaAdCopy: { ...prev.metaAdCopy, headlines: next } };
        case 'meta_description':
          return { ...prev, metaAdCopy: { ...prev.metaAdCopy, descriptions: next } };
        case 'google_headline':
          return {
            ...prev,
            googleAssets: {
              ...prev.googleAssets,
              headlines: next.map((text, index) => ({
                text,
                pinPosition: prev.googleAssets.headlines[index]?.pinPosition ?? null,
              })),
            },
          };
        case 'google_description':
          return { ...prev, googleAssets: { ...prev.googleAssets, descriptions: next } };
      }
    });
  }

  // On create: when the user picks a brand + landingPageType, fetch seeded
  // defaults from the server (Google headlines 1-3, path1,
  // from the brand's FranchiseProfile). Only seeds once the admin touches
  // the brand/type selectors; never overwrites after editing has started.
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  useEffect(() => {
    if (mode.kind !== 'create') return;
    if (defaultsLoaded) return;
    if (!state.brandAccountId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await getCampaignDefaults(state.brandAccountId, state.landingPageType);
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          customLandingPageUrl:
            prev.customLandingPageUrl ||
            (d.customLandingPageUrl ?? ''),
          metaAdCopy:
            prev.metaAdCopy.bodyCopy.length === 0 && prev.metaAdCopy.headlines.length === 0
              ? d.metaAdCopy
              : prev.metaAdCopy,
          googleAssets:
            prev.googleAssets.headlines.length === 0 && prev.googleAssets.path1 === ''
              ? d.googleAssets
              : prev.googleAssets,
          googleLandingPageOverride: d.googleLandingPageOverride,
          metaLandingPageOverride: d.metaLandingPageOverride,
          utmConfig: d.utmConfig,
        }));
        setDefaultsLoaded(true);
      } catch (err) {
        if (!cancelled) setDefaultsLoaded(true);
        console.warn('[campaigns] defaults fetch failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode.kind, state.brandAccountId, state.landingPageType, defaultsLoaded]);

  const brandAccounts = useMemo<Account[]>(() => {
    if (!accountsQuery.data) return [];
    return accountsQuery.data.filter((a) => a.accountType === 'brand');
  }, [accountsQuery.data]);

  // Pre-P2b-3 §5: when the AM changes the brand picker, the tree-picker
  // root changes too — any previously-selected shareTargets that were
  // descendants of the old brand are no longer in the new tree's scope.
  // Reset shareTargets to [newBrand] and surface a brief warning so the
  // AM knows their selection was wiped. State machine: setBrandWarning
  // is cleared on the next brand pick or on submit.
  const [brandWarning, setBrandWarning] = useState<string | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'name' && !prev.slugTouched && mode.kind === 'create') {
        next.slug = slugify(value as string);
      }
      if (key === 'brandAccountId') {
        // Trigger FranchiseProfile refetch when the brand changes.
        setBrandIdForProfile((value as string) || null);
        const nextBrand = (value as string) || '';
        if (nextBrand !== prev.brandAccountId) {
          // Reset shareTargets to [newBrand]. Tree picker is rooted at
          // the new brand; previous selections (which were under the old
          // brand) wouldn't render anyway.
          next.shareTargets = nextBrand ? [{ accountId: nextBrand }] : [];
          if (prev.shareTargets.length > 0) {
            setBrandWarning(
              "Brand changed — share targets reset to the new brand. Add specific groups or locations from the Visibility section if you want a narrower share.",
            );
          }
        }
      }
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!state.name.trim()) throw new Error('Name is required.');
      if (!state.brandAccountId) throw new Error('Brand is required.');

      // Multi Lead Form spec §1.1. Server requires a non-empty
      // campaignLeadForms[] + a defaultLeadFormId that matches one of the
      // entry ids. Both invariants are seeded in form-state initialisers
      // (blankCreateState / initialStateFromCampaign) so they're never
      // empty here in practice — but defend the boundary so a stale state
      // doesn't silently land a malformed payload.
      if (state.campaignLeadForms.length === 0 || !state.defaultLeadFormId) {
        throw new Error('At least one lead form (with a default) is required.');
      }

      const payload = {
        channels: state.channels,
        name: state.name.trim(),
        slug: state.slug.trim() || undefined,
        description: state.description.trim() || null,
        landingPageType: state.landingPageType,
        customLandingPageUrl:
          state.landingPageType === 'custom' ? state.customLandingPageUrl : null,
        metaAdCopy: state.metaAdCopy,
        googleAssets: state.googleAssets,
        campaignLeadForms: state.campaignLeadForms,
        defaultLeadFormId: state.defaultLeadFormId,
        googleLandingPageOverride: state.googleLandingPageOverride,
        metaLandingPageOverride: state.metaLandingPageOverride,
        metaCreativeFolderName: state.metaCreativeFolderName.trim() || null,
        metaAssetsFolderUrl: state.metaAssetsFolderUrl.trim() || null,
        metaAssetLabels: state.metaAssetLabels,
        offerFacts: state.offerFacts,
        googleAssetsFolderUrl: state.googleAssetsFolderUrl.trim() || null,
        otherAssetsFolderUrl: state.otherAssetsFolderUrl.trim() || null,
        utmConfig: state.utmConfig,
        campaignDescriptor: state.campaignDescriptor.trim() || null,
        metaCampaignNameTemplate: state.metaCampaignNameTemplate.trim() || null,
        googleAdLabelTemplate: state.googleAdLabelTemplate.trim() || null,
      };

      // Step 1: content save (create or PATCH). On an already-active campaign
      // the server re-runs active-state validation during PATCH; any missing
      // fields come back before any status flip happens.
      // Server requires min 1 share target — guard before submit so the
      // user gets an inline error rather than a 400 round-trip.
      if (state.shareTargets.length === 0) {
        throw new Error('At least one share target is required.');
      }
      let saved: Campaign;
      if (mode.kind === 'create') {
        const created: CampaignCreatePayload = {
          createdInBrandAccountId: state.brandAccountId,
          shareTargets: state.shareTargets,
          ...payload,
        };
        saved = await createCampaign(created);
      } else {
        const patch: CampaignUpdatePayload = {
          ...payload,
          shareTargets: state.shareTargets,
        };
        saved = await updateCampaign(mode.campaign.id, patch);
      }

      // Step 2: status transition if the user picked a different target
      // (draft -> active, active -> archived, etc.). Validation for activate
      // runs on the stored content; a 400 with a missing[] list surfaces
      // field-level bullets. On failure we throw but the content IS saved.
      if (targetStatus !== saved.status) {
        try {
          saved = await changeCampaignStatus(saved.id, targetStatus);
        } catch (err) {
          if (err instanceof ApiError && err.missing.length > 0) {
            // Content saved, status flip failed. Preserve the id so we can
            // navigate to detail if the user dismisses, and surface the list.
            const e = new Error(err.message);
            (e as Error & { missing?: MissingField[]; savedId?: string }).missing = err.missing;
            (e as Error & { missing?: MissingField[]; savedId?: string }).savedId = saved.id;
            throw e;
          }
          throw err;
        }
      }

      return saved;
    },
    onSuccess: (campaign) => {
      navigate(`/campaigns/${campaign.id}`);
    },
    onError: (e: Error) => {
      setError(e.message);
      const maybeMissing = (e as Error & { missing?: MissingField[] }).missing;
      setMissing(Array.isArray(maybeMissing) ? maybeMissing : []);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMissing([]);
    mutation.mutate();
  };

  const saveLabel = (() => {
    if (mutation.isPending) return 'Saving...';
    if (mode.kind === 'create') {
      if (targetStatus === 'active') return 'Create & activate';
      if (targetStatus === 'archived') return 'Create & archive';
      return 'Create as draft';
    }
    if (targetStatus === 'active' && mode.campaign.status !== 'active') return 'Save & activate';
    if (targetStatus === 'archived' && mode.campaign.status !== 'archived') return 'Save & archive';
    if (targetStatus === 'draft' && mode.campaign.status !== 'draft') return 'Save & revert to draft';
    if (targetStatus === 'inactive' && mode.campaign.status !== 'inactive') return 'Save & deactivate';
    return 'Save changes';
  })();

  const STATUS_OPTIONS: CampaignStatus[] = ['draft', 'active', 'inactive', 'archived'];

  if (accountsQuery.isLoading) return <CenteredSpinner />;
  if (accountsQuery.error) {
    return <div className="text-red-600 text-sm">Failed to load accounts.</div>;
  }

  const isEdit = mode.kind === 'edit';

  return (
    <form onSubmit={onSubmit} className="max-w-5xl mx-auto space-y-6">
      <div className="bg-white rounded-md border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-900">Save as:</span>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className={`px-3 py-1.5 rounded-md text-sm ${
                targetStatus === s
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
              }`}
              onClick={() => setTargetStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 ml-auto">
          {targetStatus === 'active'
            ? 'Activating runs full validation; missing fields are listed above Save.'
            : targetStatus === 'archived'
              ? 'Archived campaigns stay editable; not pickable in new rollouts.'
              : 'Drafts save freely with partial data.'}
        </p>
      </div>

      {/* Round 2: section stack lives inside a grid with the sticky
          nav. Save-as bar above and error/buttons block below stay
          full-width — only the section column is constrained by the
          200-px nav next to it. */}
      <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-8">
        <StickyNav
          ariaLabel="Campaign Builder sections"
          sections={SECTION_NAV.filter(
            (s) =>
              (showMeta || !META_SECTION_IDS.has(s.id)) &&
              (showGoogle || !GOOGLE_SECTION_IDS.has(s.id)),
          )}
          actions={
            // Round 3 (Fix 2): mirror the bottom Save / Cancel
            // buttons inside the sticky nav so editors don't have
            // to scroll back down on long forms. Same handlers,
            // same disabled state, same submit-on-form-context
            // semantics. The bottom row stays in place.
            <>
              <Button
                type="submit"
                disabled={mutation.isPending}
                className="w-full"
              >
                {saveLabel}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  navigate(isEdit ? `/campaigns/${mode.campaign.id}` : '/campaigns')
                }
                className="w-full"
              >
                Cancel
              </Button>
            </>
          }
        />
        <div className="min-w-0 space-y-6">
      <Section id="cf-overview" title="Overview">
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Brand</label>
            <Combobox
              aria-label="Brand"
              placeholder="Select a brand..."
              searchPlaceholder="Search brands…"
              value={state.brandAccountId}
              onValueChange={(v) => update('brandAccountId', v)}
              disabled={isEdit}
              options={[
                { value: '', label: 'Select a brand...' },
                ...brandAccounts.map((a) => ({ value: a.id, label: a.name })),
              ]}
            />
            {isEdit && (
              <p className="text-xs text-slate-500">Brand is immutable after create.</p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Channels</label>
            <SelectField
              aria-label="Channels"
              value={state.channels}
              onValueChange={(v) =>
                setState((prev) => applyChannelChange(prev, v as CampaignChannels))
              }
              options={CHANNEL_OPTIONS}
            />
            <p className="text-xs text-slate-500">
              Meta only or Google only hides the other channel's sections. Anything already
              entered there is kept, so switching back restores it.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-900">Name</label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={state.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="50% off first month"
              />
              <p className="text-xs text-slate-500">
                The Campaign name doubles as the human-readable offer description.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-900">Slug</label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={state.slug}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }))
                }
                placeholder="auto-from-name"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Description</label>
            <textarea
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={state.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Short internal summary"
            />
          </div>

          {/* Pre-P2b-3 polish round 5 §2.4: descriptor moved to
              Overview from "Meta Naming". The descriptor is shared
              between Meta naming AND Google ad-labels (round 5 §2.1
              rename), so it lives at the Campaign Overview level
              now. Both naming templates' {campaignDescriptor}
              placeholders resolve from this single value. */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">
              Campaign descriptor
              <Tooltip text="Short identifier used in both the Meta naming template AND the Google ad-label template, e.g. '6 Weeks Free'." />
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={state.campaignDescriptor}
              onChange={(e) => update('campaignDescriptor', e.target.value)}
              placeholder="6 Weeks Free"
              maxLength={200}
            />
          </div>

          {/* Consolidation §5.4: Offer Mechanics + Kyck Campaign Overview
              URL inputs removed — these now live on the Promotion Plan. */}

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Landing page type</label>
            <SelectField
              aria-label="Landing page type"
              value={state.landingPageType}
              onValueChange={(v) => update('landingPageType', v as LandingPageType)}
              options={LANDING_TYPES.map((t) => ({ value: t, label: t }))}
            />
            {state.landingPageType !== 'custom' && (
              <LandingPagePreview
                resolved={resolveMasterLanding(state.landingPageType, null, templates)}
              />
            )}
          </div>

          {state.landingPageType === 'custom' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-900">Custom landing page URL</label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={state.customLandingPageUrl}
                onChange={(e) => update('customLandingPageUrl', e.target.value)}
                placeholder="https://example.com/offer/{suburbSlug}"
              />
              <PlaceholderHint />
              <LandingPagePreview
                resolved={resolveMasterLanding('custom', state.customLandingPageUrl, templates)}
              />
            </div>
          )}
        </div>
      </Section>

      <Section id="cf-visibility" title="Visibility">
        <p className="text-xs text-slate-500">
          Pick the brand, groups, and locations that can use this Campaign. A
          Campaign is visible to a location when any selected target is the
          location itself or one of its ancestors.
        </p>
        {brandWarning && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {brandWarning}
          </div>
        )}
        {state.brandAccountId ? (
          <AccountTreePicker
            rootAccountId={state.brandAccountId}
            value={state.shareTargets.map((t) => t.accountId)}
            onChange={(next) =>
              update(
                'shareTargets',
                next.map((accountId) => ({ accountId })),
              )
            }
          />
        ) : (
          <p className="text-sm text-slate-500 italic">
            Pick a brand above to configure visibility.
          </p>
        )}
        {state.brandAccountId && state.shareTargets.length === 0 && (
          <p className="text-xs text-red-600">
            At least one share target is required.
          </p>
        )}
      </Section>

      {/* Pre-P2b-3 polish round 5 §2.4: Meta Naming now holds just
          the per-Campaign template override. Descriptor moved to
          Overview (shared with Google labels). */}
      {showMeta && (
      <Section id="cf-meta-naming" title="Meta naming">
        <p className="text-xs text-slate-500">
          The Meta campaign name shown to media buyers in Ads Manager. The descriptor
          (set in Overview) drops into the brand's naming template; if you set a
          template here it overrides the brand default.
        </p>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-900">
            Per-Campaign template override (optional)
            <Tooltip text="Leave blank to use the brand's default Meta naming template. Set to override for this Campaign only." />
          </label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={state.metaCampaignNameTemplate}
            onChange={(e) => update('metaCampaignNameTemplate', e.target.value)}
            placeholder="{locationName} - {campaignDescriptor} - {monthYear}"
            maxLength={500}
          />
          <p className="text-xs text-slate-500">
            Supports{' '}
            <code className="font-mono">{'{locationName}'}</code>,{' '}
            <code className="font-mono">{'{campaignDescriptor}'}</code>,{' '}
            <code className="font-mono">{'{monthYear}'}</code>, and the standard
            location/brand placeholders. Resolved per-location at render time.
          </p>
        </div>
      </Section>
      )}

      {/* Pre-P2b-3 polish round 5 §2.2 + §2.4: Google ad-label template,
          parallel structure to Meta Naming above. The descriptor is
          shared (lives in Overview); only the template differs per
          platform. */}
      {showGoogle && (
      <Section id="cf-google-naming" title="Google naming">
        <p className="text-xs text-slate-500">
          The label media buyers paste into Google Ads to identify this Campaign.
          Same descriptor as Meta (set in Overview); the template here overrides
          the brand's default Google ad-label template if set.
        </p>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-900">
            Per-Campaign template override (optional)
            <Tooltip text="Leave blank to use the brand's default Google ad-label template. Set to override for this Campaign only." />
          </label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={state.googleAdLabelTemplate}
            onChange={(e) => update('googleAdLabelTemplate', e.target.value)}
            placeholder="{campaignDescriptor} - {monthYear}"
            maxLength={500}
          />
          <p className="text-xs text-slate-500">
            Supports the same placeholder set as Meta naming. Resolved per-location
            at render time.
          </p>
        </div>
      </Section>
      )}

      {/* ⚠️ ONE trigger for all five outputs, sitting above the first copy
          section. The AM is writing ONE campaign, not five fields — a per-field
          button would mean five Opus calls to fill one campaign, and five
          separate briefs describing the same offer. */}
      <div id="cf-ai-copy" className="rounded-lg border border-kyck-200 bg-kyck-50/30 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <CampaignCopyTrigger
            brandAccountId={state.brandAccountId}
            onOpen={() => setCopyOpen(true)}
          />
          <p className="text-xs text-slate-600">
            Writes options for the Meta and Google fields below, in this brand&apos;s voice, and
            checks every Google line against your longest suburb before offering it.
          </p>
        </div>
      </div>

      <CampaignCopyWorkspace
        open={copyOpen}
        onOpenChange={setCopyOpen}
        brandAccountId={state.brandAccountId}
        campaignName={state.name}
        campaignDescription={state.description.trim() === '' ? null : state.description}
        campaignDescriptor={
          state.campaignDescriptor.trim() === '' ? null : state.campaignDescriptor
        }
        assetLabels={state.metaAssetLabels}
        values={copyValues}
        onInsert={applyCopy}
        offerFacts={state.offerFacts}
        onOfferFactsChange={(facts) => setState((prev) => ({ ...prev, offerFacts: facts }))}
      />

      {showMeta && (
      <Section id="cf-meta" title="Meta ad copy">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-900">Meta ad type</label>
          <SelectField
            aria-label="Meta ad type"
            value={state.metaAdCopy.metaAdType}
            onValueChange={(v) =>
              update('metaAdCopy', {
                ...state.metaAdCopy,
                metaAdType: v as MetaAdType,
              })
            }
            options={META_AD_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
          <p className="text-xs text-slate-500">
            {META_AD_TYPE_OPTIONS.find((o) => o.value === state.metaAdCopy.metaAdType)?.blurb}
          </p>
        </div>

        <BodyCopyArrayEditor
          values={state.metaAdCopy.bodyCopy}
          onChange={(v) => update('metaAdCopy', { ...state.metaAdCopy, bodyCopy: v })}
        />
        <HeadlineArrayEditor
          values={state.metaAdCopy.headlines}
          onChange={(v) => update('metaAdCopy', { ...state.metaAdCopy, headlines: v })}
        />
        <DescriptionArrayEditor
          values={state.metaAdCopy.descriptions}
          onChange={(v) => update('metaAdCopy', { ...state.metaAdCopy, descriptions: v })}
        />
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-900">CTA button</label>
          <SelectField
            aria-label="CTA button"
            className="w-auto min-w-[12rem]"
            value={state.metaAdCopy.ctaButton}
            onValueChange={(v) =>
              update('metaAdCopy', {
                ...state.metaAdCopy,
                ctaButton: v as CtaOption,
              })
            }
            options={CTA_OPTIONS.map((cta) => ({ value: cta, label: cta }))}
          />
        </div>

        {/* Multi Lead Form spec §1.1. Lead form authoring is now a
            separate section below (commit 2 lands the tab strip).
            Commit 1 reuses the existing single-form editor on the first
            (and only seeded) campaignLeadForms entry. The Meta ad type
            gate still applies — non-lead_gen ad types don't display
            lead form UI here, but the underlying campaignLeadForms[]
            still ships with the Campaign so it can flip to lead_gen
            later without re-authoring. */}

        {(state.metaAdCopy.metaAdType === 'traffic' || state.metaAdCopy.metaAdType === 'both') && (
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-900">Meta landing page</h3>
            <LandingPageOverrideEditor
              label="Override (used when Meta runs traffic ads)"
              value={state.metaLandingPageOverride}
              onChange={(v) => update('metaLandingPageOverride', v)}
              masterType={state.landingPageType}
              masterCustomUrl={state.customLandingPageUrl || null}
              templates={templates}
            />
          </div>
        )}
      </Section>
      )}

      {/* Multi Lead Form spec §2. Lead form authoring section. The tab
          strip handles +/rename/delete/set-as-default; rules (at least
          one form, can't delete default) are enforced by the pure
          helpers in lib/campaignLeadForms.ts. Always renders — even
          for traffic-only Campaigns — so flipping back to lead_gen
          doesn't lose authored forms. */}
      {showMeta && (
      <Section id="cf-lead-forms" title="Lead forms">
        {state.campaignLeadForms.length === 0 || !state.defaultLeadFormId ? (
          <p className="text-sm text-slate-400 italic">No lead forms.</p>
        ) : (
          <LeadFormTabStrip
            value={state.campaignLeadForms}
            defaultLeadFormId={state.defaultLeadFormId}
            onChange={(forms, defaultId) => {
              setState((prev) => ({
                ...prev,
                campaignLeadForms: forms,
                defaultLeadFormId: defaultId,
              }));
            }}
          />
        )}
      </Section>
      )}

      {showGoogle && (
      <Section id="cf-google" title="Google assets">
        <GoogleAssetsEditor
          value={state.googleAssets}
          onChange={(v) => update('googleAssets', v)}
        />
        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Google landing page</h3>
          <LandingPageOverrideEditor
            label="Override (derives Google's final URL at rollout snapshot)"
            value={state.googleLandingPageOverride}
            onChange={(v) => update('googleLandingPageOverride', v)}
            masterType={state.landingPageType}
            masterCustomUrl={state.customLandingPageUrl || null}
            templates={templates}
          />
        </div>
      </Section>
      )}

      <Section id="cf-assets" title="Asset folders">
        <p className="text-xs text-slate-500">
          Three separate Drive folders. The Meta folder is browsable inline (Phase 1 of P2c)
          — pick files, label them, and the labels carry through to every rollout's row at
          generation. The Google and Other folders are plain link passthroughs.
        </p>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">
              Meta Creative Folder Name
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={state.metaCreativeFolderName}
              onChange={(e) =>
                update('metaCreativeFolderName', e.target.value)
              }
              placeholder="e.g. Snap AU – June Crossword"
            />
            <p className="text-xs text-slate-500">
              Used for ad naming. Visible to Meta team during build.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Meta assets folder URL</label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={state.metaAssetsFolderUrl}
              onChange={(e) => update('metaAssetsFolderUrl', e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
            />
            <DriveAssetsBrowser
              mode="edit"
              title="Meta Assets"
              folderUrl={state.metaAssetsFolderUrl}
              value={state.metaAssetLabels}
              onChange={(next) => update('metaAssetLabels', next)}
              // Ad Preview Mockups Stage 1 §2.1: Campaign-level Meta Assets
              // get the adType dropdown so each label's classification
              // flows downstream to every rolled-out row.
              showAdType
              // Stage 1 §13.15. Fire-and-forget stub-write so the
              // dropdown's detected adType also lands in Firestore
              // (visible to the public preview + other surfaces that
              // read Campaign.metaAssetLabels). Only meaningful in
              // EDIT mode — fresh-create has no campaign id yet, so
              // those stubs stay in form state and persist with the
              // AM's first explicit Save (which audits normally).
              onStubsDetected={
                mode.kind === 'edit'
                  ? (stubs) => {
                      const campaignId = mode.campaign.id;
                      addCampaignAssetLabelStubs(campaignId, stubs).catch(
                        (err) => {
                          // Graceful degradation per spec §13.15 —
                          // failures don't break the AM's flow; the
                          // dropdown still shows the detected value
                          // and the next AM-initiated save persists
                          // through the normal audited update path.
                          console.warn(
                            '[adType-detect] stub-write failed',
                            err,
                          );
                        },
                      );
                    }
                  : undefined
              }
              // Stage 1 §13.17. Wire the prune button + modal. Only
              // meaningful in EDIT mode — fresh-create has no campaign
              // id yet and no orphans to prune. After the server-side
              // prune completes, DriveAssetsBrowser auto-filters the
              // local `value` to drop orphans (via onChange), so the
              // form state matches what the server just wrote.
              pruneTarget={
                mode.kind === 'edit'
                  ? {
                      kind: 'campaign' as const,
                      campaignId: mode.campaign.id,
                      campaignName: mode.campaign.name,
                    }
                  : undefined
              }
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Google assets folder URL</label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={state.googleAssetsFolderUrl}
              onChange={(e) => update('googleAssetsFolderUrl', e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Other assets folder URL</label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={state.otherAssetsFolderUrl}
              onChange={(e) => update('otherAssetsFolderUrl', e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
            />
          </div>
        </div>
      </Section>

      <Section id="cf-utm" title="UTM tags">
        <UtmConfigEditor
          value={state.utmConfig}
          onChange={(v) => update('utmConfig', v)}
        />
      </Section>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-2">
          <p>{error}</p>
          {missing.length > 0 && (
            <ul className="list-disc pl-5 space-y-0.5">
              {missing.map((m, i) => (
                <li key={i}>
                  <code className="font-mono text-xs">{m.path}</code> — {m.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <Button type="submit" disabled={mutation.isPending}>
          {saveLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(isEdit ? `/campaigns/${mode.campaign.id}` : '/campaigns')}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
